import { useState } from 'react';
import AssessmentList from './AssessmentList';
import AssessmentFormDialog from './AssessmentFormDialog';
import DeleteConfirmDialog from '@/components/dialogs/DeleteConfirmDialog';
import type { PublicAssessment } from '@arrowise/shared';
import { useAuth } from '@/providers/AuthProvider';
import assessmentService from '@/services/assessmentService';

interface AssessmentTabProps {
    sessionId: string;
    teamId?: string;
    assessments: PublicAssessment[];
    loadingAssessments: boolean;
    onLoadAssessments: () => void;
    isGraded: boolean;
}

export default function AssessmentTab({
    sessionId,
    teamId,
    assessments,
    loadingAssessments,
    onLoadAssessments,
    isGraded
}: AssessmentTabProps) {
    const { user } = useAuth();
    const [assessmentDialogOpen, setAssessmentDialogOpen] = useState(false);
    const [userAssessment, setUserAssessment] = useState<PublicAssessment | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [assessmentToDelete, setAssessmentToDelete] = useState<PublicAssessment | null>(null);

    // Handle successful assessment creation/update
    const handleAssessmentSuccess = () => {
        onLoadAssessments();
    };

    // Handle edit assessment
    const handleEditAssessment = (assessment: PublicAssessment) => {
        setUserAssessment(assessment);
        setAssessmentDialogOpen(true);
    };

    // Handle delete assessment
    const handleDeleteAssessment = (assessment: PublicAssessment) => {
        setAssessmentToDelete(assessment);
        setDeleteDialogOpen(true);
    };

    // Confirm delete assessment
    const confirmDeleteAssessment = async () => {
        if (!assessmentToDelete || !sessionId) return;

        try {
            await assessmentService.deleteAssessment(sessionId, assessmentToDelete.assessorId);
            onLoadAssessments();
            setDeleteDialogOpen(false);
            setAssessmentToDelete(null);
        } catch (error) {
            console.error('Failed to delete assessment:', error);
            alert('Failed to delete assessment');
        }
    };

    return (
        <div className="space-y-4">
            <AssessmentList
                assessments={assessments}
                loading={loadingAssessments}
                onAssessClick={() => {
                    setUserAssessment(null);
                    setAssessmentDialogOpen(true);
                }}
                currentUserId={user?.id}
                teamId={teamId}
                onEditAssessment={handleEditAssessment}
                onDeleteAssessment={handleDeleteAssessment}
                isGraded={isGraded}
            />

            {/* Assessment Dialog */}
            <AssessmentFormDialog
                open={assessmentDialogOpen}
                onOpenChange={setAssessmentDialogOpen}
                sessionId={sessionId}
                existingAssessment={userAssessment}
                onSuccess={handleAssessmentSuccess}
            />

            {/* Delete Confirmation Dialog */}
            <DeleteConfirmDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                title="Delete Assessment"
                description={
                    assessmentToDelete ? (
                        <div className="space-y-2">
                            <p>Are you sure you want to delete the assessment by <strong>{assessmentToDelete.assessorName}</strong>?</p>
                            <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
                        </div>
                    ) : (
                        "Are you sure you want to delete this assessment?"
                    )
                }
                confirmLabel="Delete Assessment"
                onConfirm={confirmDeleteAssessment}
            />
        </div>
    );
}
